import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import confetti from "canvas-confetti";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIRST_MS = 60000;
const CROSS_S = 1.9;

const isNight = () => {
  const h = new Date().getHours();
  return window.location.search.includes("night") || window.location.search.includes("star") || h >= 20 || h < 6;
};

// Tiny WebAudio synth — no asset files needed.
let audioCtx;
const tone = (f0, f1, dur, type = "sine", gain = 0.08) => {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === "suspended") audioCtx.resume();
    const o = audioCtx.createOscillator();
    const g = audioCtx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(f0, audioCtx.currentTime);
    o.frequency.exponentialRampToValueAtTime(f1, audioCtx.currentTime + dur);
    g.gain.setValueAtTime(gain, audioCtx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + dur);
    o.connect(g); g.connect(audioCtx.destination);
    o.start(); o.stop(audioCtx.currentTime + dur);
  } catch { /* audio blocked until user gesture — fine */ }
};
const whoosh = () => tone(900, 160, 0.8, "sawtooth", 0.028);
const chime = () => { tone(880, 1760, 0.35, "sine", 0.1); setTimeout(() => tone(1320, 2640, 0.45, "sine", 0.08), 130); };

// 🌠 SHOOTING STARS — rare, FAST night-sky catch. +5 POG BUCKS for quick tappers (3/night).
export const ShootingStar = () => {
  const [star, setStar] = useState(null);
  const [toast, setToast] = useState(null);
  const toastT = useRef(null);
  const night = useRef(isNight());

  useEffect(() => {
    if (!night.current) return undefined;
    let t;
    const fire = () => {
      setStar({ key: Date.now(), top: 6 + Math.random() * 22 });
      whoosh();
      setTimeout(() => setStar(null), (CROSS_S + 0.4) * 1000);
      t = setTimeout(fire, (120 + Math.random() * 150) * 1000);
    };
    t = setTimeout(fire, window.location.search.includes("star") ? 3000 : FIRST_MS);
    return () => clearTimeout(t);
  }, []);

  const say = (msg) => {
    setToast(msg);
    clearTimeout(toastT.current);
    toastT.current = setTimeout(() => setToast(null), 3200);
  };

  const catchStar = async (s) => {
    setStar(null);
    chime();
    confetti({ particleCount: 50, spread: 70, scalar: 0.8, colors: ["#fde68a", "#fff", "#93c5fd"], origin: { x: 0.5, y: s.top / 100 + 0.06 } });
    try {
      const r = await axios.post(`${API}/shooting-star/tap`, {}, { withCredentials: true });
      say(r.data.message);
    } catch { say("🌠!"); }
  };

  return (
    <AnimatePresence>
      {star && (
        <motion.button key={star.key} data-testid="shooting-star"
          onClick={() => catchStar(star)}
          initial={{ x: "110vw", y: 0, opacity: 0 }}
          animate={{ x: "-15vw", y: "16vh", opacity: 1 }}
          exit={{ opacity: 0, scale: 1.6 }}
          transition={{ x: { duration: CROSS_S, ease: "linear" }, y: { duration: CROSS_S, ease: "linear" }, opacity: { duration: 0.15 } }}
          className="absolute z-[1845] pointer-events-auto select-none cursor-pointer p-3"
          style={{ top: `${star.top}%`, left: 0 }}>
          <span className="block text-4xl" style={{ filter: "drop-shadow(0 0 16px rgba(253,230,138,0.9))" }}>🌠</span>
        </motion.button>
      )}
      {toast && (
        <motion.div data-testid="star-toast" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className="absolute bottom-44 left-1/2 -translate-x-1/2 z-[1860] bg-slate-900/95 border border-amber-300/60 rounded-full px-4 py-2 text-sm font-heading font-bold text-amber-200 shadow-xl pointer-events-none whitespace-nowrap">
          {toast}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
